import { ModelBuilder, Warning } from "@carbon/icons-react";
import { IconButton, Tooltip } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import { FC } from "react";
import { generatePath, Link } from "react-router-dom";

import { CaseArchivedTag } from "src/components/case/CaseArchivedTag";
import { ModalTransition } from "src/components/Modal";
import IdentifierGraphModal from "src/dynamic-view-renderer/Widgets/RelatedCases/IdentifierGraph/IdentifierGraphModal.tsx";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import { useT } from "src/internationalization";

import { useWidgetDefinition } from "./useWidgetDefinition";

export type PropertyValueRendererParams = {
  CaseLink?: {
    id: string;
    caseTypeCode: string;
  };
  Identifier?: {
    snapshotCreation: Date | null;
  };
  IdentifierGraph?: {
    snapshotCreation: Date | null;
  };
};

type PropertyValueProps = {
  params?: PropertyValueRendererParams;
} & WidgetProperty;

export const PropertyValueRenderer: FC<PropertyValueProps> = ({
  type,
  params,
  value,
  blacklist,
  ...rest
}) => {
  const widgetDefinition = useWidgetDefinition<RelatedWidgetConfiguration>("relatedCases");

  const t = useT();
  const { isModalOpen, closeModal, openModal } = useModal();

  switch (type) {
    case "CaseLink":
      return params?.CaseLink ? (
        <Link
          to={generatePath("/case/:caseTypeCode/:id", {
            caseTypeCode: params.CaseLink.caseTypeCode,
            id: params.CaseLink.id,
          })}
          className="no-underline"
        >
          {value}
        </Link>
      ) : (
        value
      );
    case "Identifier":
    case "IdentifierGraph":
      return (
        <>
          <div className="flex gap-3 items-center">
            <div className="text-sm"> {value}</div>
            {blacklist && (
              <Tooltip
                align="bottom-right"
                as={"p"}
                label={
                  <div className={"flex flex-col items-start"}>
                    <div>
                      {t("component.widget.basic.property.blacklist.tooltip", { blacklist })}
                    </div>
                    {params &&
                      params.IdentifierGraph &&
                      params.IdentifierGraph.snapshotCreation && (
                        <CaseArchivedTag date={params.IdentifierGraph.snapshotCreation} />
                      )}
                  </div>
                }
                className="text-sm block h-5"
              >
                <Warning className="text-supportError" />
              </Tooltip>
            )}
            {/* Using invisible class instead of conditional rendering to maintain consistent row heights */}
            <IconButton
              align="bottom-right"
              size="sm"
              label={t("widget.identifierRelatedCasesGraph.iconTooltip")}
              kind="ghost"
              onClick={openModal}
              className={`${type !== "IdentifierGraph" ? "invisible" : ""}`}
              wrapperClasses={`cds--popover--open [&>div]:group [&>span]:opacity-0 ${
                type === "IdentifierGraph" ? "[&>div:hover+span]:opacity-100" : ""
              }`}
            >
              <ModelBuilder className="text-supportInfo" />
            </IconButton>
          </div>

          <ModalTransition>
            {isModalOpen && (
              <IdentifierGraphModal
                subTitle={
                  <div>
                    <div>
                      {t("widget.identifierRelatedCasesGraph.modalTitle")}: {value}
                    </div>
                    {params &&
                      params.IdentifierGraph &&
                      params.IdentifierGraph.snapshotCreation && (
                        <CaseArchivedTag date={params.IdentifierGraph.snapshotCreation} />
                      )}
                  </div>
                }
                closeModal={closeModal}
                configuration={
                  widgetDefinition?.configuration?.customConfiguration?.matchElementsColumns?.filter(
                    (elem) => elem.code === rest.elementCode
                  ) ?? []
                }
                identifier={{
                  type,
                  value,
                  blacklist,
                  ...rest,
                }}
              />
            )}
          </ModalTransition>
        </>
      );

    default:
      return value;
  }
};
